import React, { useEffect } from 'react';
import { LinearProgress, styled, Toolbar, Typography, Tooltip, IconButton, ButtonGroup, Button } from '@mui/material';
import { DefaultActionDefinition, Folder, Image } from 'models';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import * as FolderStore from '../store/Folder';
import * as ImageStore from '../store/Image';
import FilesTable from './FilesTable';
import * as DialogFactory from 'services/Dialog.Factory';
import { DialogProviderContext } from './DialogProvider';
import { DialogProviderContextModel } from 'models';
import MessageView from './MessageView';
import { useNavigate } from 'react-router-dom';
import ImageUploader from 'components/ImageUploader';

import RefreshIcon from '@mui/icons-material/RefreshOutlined';
import CreateFolderIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import MoveIcon from '@mui/icons-material/DriveFileMoveOutlined';
import CreateIcon from '@mui/icons-material/Create';
import FilesGrid from './FilesGrid';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ListOutlinedIcon from '@mui/icons-material/ListOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

const DefaultToolbar = styled(Toolbar)(({ theme }) => ({
    paddingLeft: '12px !important',
    paddingRight: theme.spacing(1),
    height: 64
}));

const ImagesSelectedToolbar = styled(Toolbar)(({ theme }) => ({
    paddingLeft: '12px !important',
    paddingRight: theme.spacing(1),
    height: 64,
    backgroundColor: '#EEE'
}));

const ToolbarTitle = styled(Typography)(({ theme }) => ({
    flex: '1 1 100%'
}));

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
    marginLeft: theme.spacing(1),
}));

const StyledButtonGroupButton = styled(Button)(({ theme }) => ({
    color: theme.palette.grey[600],
    backgroundColor: theme.palette.grey[500]
}));

interface FolderViewHeaderProps {
    folder: Folder;
    images: Image[];
    selectedImageIds: string[];
    view: 'list' | 'grid';
    onRefresh: (folder: Folder) => void;
    onCreateFolder: (parentFolder: Folder) => void;
    onDeleteFolder: (folder: Folder) => void;
    onDeleteImages: (images: Image[]) => void;
    onMoveImages: (images: Image[]) => void;
    onMoveFolder: (folder: Folder) => void;
    onRenameFolder: (folder: Folder) => void;
    onChangeView: (view: 'list' | 'grid') => void;
    onDeselectAll: () => void;
}

const FolderViewHeader = (props: FolderViewHeaderProps) => {
    const { folder, images, selectedImageIds, view } = props;

    const handleRefresh = () => props.onRefresh(folder);
    const handleCreateFolder = () => props.onCreateFolder(folder);
    const handleDeleteFolder = () => props.onDeleteFolder(folder);
    const handleMoveFolder = () => props.onMoveFolder(folder);
    const handleRenameFolder = () => props.onRenameFolder(folder);
    const handleGridButtonClick = () => props.onChangeView('grid');
    const handleListButtonClick = () => props.onChangeView('list');

    const handleDeleteSelectedImages = () => {
        const deleteImages = images.filter(i => selectedImageIds.includes(i.id))
        if (deleteImages.length < 1) {
            console.error('Cannot delete selected images because none are selected.')
            return;
        }
        props.onDeleteImages(deleteImages);
    }

    const handleMoveSelectedImages = () => {
        const moveImages = images.filter(i => selectedImageIds.includes(i.id));
        if (moveImages.length < 1) {
            console.error('Cannot move selected images because none are selected.')
            return;
        }
        props.onMoveImages(moveImages);
    }

    return (
        <>
            {
                selectedImageIds.length <= 0 &&
                <DefaultToolbar>
                    <ToolbarTitle variant="h6" id="tableTitle">
                        {folder.name}
                        <Tooltip title="Rename">
                            <IconButton style={{ marginLeft: 8 }} onClick={handleRenameFolder}><CreateIcon /></IconButton>
                        </Tooltip>
                    </ToolbarTitle>
                    <ImageUploader folderId={folder.id} />
                    <Tooltip title="Create new folder">
                        <IconButton aria-label="create folder" onClick={handleCreateFolder}>
                            <CreateFolderIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Move folder">
                        <IconButton aria-label="move" onClick={handleMoveFolder}>
                            <MoveIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete folder">
                        <IconButton aria-label="delete" onClick={handleDeleteFolder}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh">
                        <IconButton aria-label="refresh" onClick={handleRefresh}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <StyledButtonGroup variant='outlined'>
                        <Tooltip title="List View">
                            <StyledButtonGroupButton style={{ borderColor: '#757575', backgroundColor: view === 'list' ? '#E8E8E8' : 'inherit' }} onClick={handleListButtonClick}><ListOutlinedIcon /></StyledButtonGroupButton>
                        </Tooltip>
                        <Tooltip title="Grid View">
                            <StyledButtonGroupButton style={{ borderColor: '#757575', backgroundColor: view === 'grid' ? '#E8E8E8' : 'inherit' }} onClick={handleGridButtonClick}><GridViewOutlinedIcon /></StyledButtonGroupButton>
                        </Tooltip>
                    </StyledButtonGroup>
                </DefaultToolbar>
            }
            {
                selectedImageIds.length > 0 &&
                <ImagesSelectedToolbar>
                    <Typography
                        sx={{ flex: '1 1 100%' }}
                        color="inherit"
                        variant="subtitle1"
                        component="div"
                    >
                        {selectedImageIds.length} images selected
                    </Typography>
                    <Tooltip title="Move images">
                        <IconButton aria-label="move" onClick={handleMoveSelectedImages}>
                            <MoveIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete images">
                        <IconButton aria-label="delete" onClick={handleDeleteSelectedImages}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                        <IconButton aria-label="cancel" onClick={props.onDeselectAll}>
                            <CloseOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                </ImagesSelectedToolbar>
            }
        </>
    );
}

const FolderView = (props: { folderId?: string }) => {
    const folders = useSelector(FolderStore.selectors.foldersFlattened);
    const allImages = useSelector(ImageStore.selectors.images);
    const folder = folders.find(f => f.id === props.folderId);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const loadImages = useSelector(ImageStore.selectors.loadImages);
    const images = folder ? ImageStore.utils.getImagesForFolder(allImages, folder.id) : [];
    const loadingFolders = useSelector(FolderStore.selectors.loadFolders);
    const loadingImages = useSelector(ImageStore.selectors.loadImages).find(li => li.id === props.folderId);
    const isLoading = loadingFolders.is || (loadingImages ? loadingImages.is : false);
    const [selectedImageIds, setSelectedImageIds] = React.useState<string[]>([]);
    const [view, setView] = React.useState<'list' | 'grid'>('list');

    useEffect(() => {
        const item = localStorage.getItem('FOLDER_VIEW');
        const value = item && item === 'grid' ? 'grid' : 'list';
        console.log('presetting', value);
        setView(value);
    }, [])

    useEffect(() => {
        // attempt to load images if none are yet
        if (!folder) {
            return;
        }
        const loading = loadImages.find(li => li.id === folder.id) || DefaultActionDefinition;
        if (!loading.did && !loading.is) {
            dispatch(ImageStore.actionCreators.loadImages(folder.id));
        }
    }, [folder, dispatch, loadImages]);

    useEffect(() => {
        // deselect any images that are no longer in the images provided
        const validImageIds = images.map(image => image.id);
        const imagesToDeselect = selectedImageIds.filter(imageId => !validImageIds.includes(imageId));
        if (imagesToDeselect.length > 0) {
            setSelectedImageIds(selectedImageIds.filter(imageId => validImageIds.includes(imageId)));
        }
    }, [images, setSelectedImageIds, selectedImageIds])

    const handleRefresh = (folder: Folder) => {
        // refresh all folders
        dispatch(FolderStore.actionCreators.loadFolders());

        // refresh all files for the current folder
        dispatch(ImageStore.actionCreators.loadImages(folder.id));
    }

    const createCreateFolderCallback = (dialogProvider: DialogProviderContextModel) => (parentFolder: Folder) => {
        const dialog = DialogFactory.buildFolderNameDialog(dialogProvider, (folderName: string) => {
            return new Promise((resolve, reject) => {
                dispatch(FolderStore.actionCreators.createFolder(folderName, parentFolder.id, (success: boolean, errors?: string[]) => {
                    if (success) {
                        resolve([]);
                        dialog.close();
                    } else {
                        resolve(errors);
                    }
                }));
            });
        });
        dialog.open();
    }

    const createRenameFolderCallback = (dialogProvider: DialogProviderContextModel) => (folder: Folder) => {
        if (!folder) {
            console.error('Cannot rename folder. Folder is null.');
            return;
        }
        const dialog = DialogFactory.buildFolderNameDialog(dialogProvider, (folderName: string) => {
            return new Promise((resolve, reject) => {
                dispatch(FolderStore.actionCreators.renameFolder(folderName, folder.id, (success: boolean, errors?: string[]) => {
                    if (success) {
                        resolve([]);
                        dialog.close();
                    } else {
                        resolve(errors);
                    }
                }));
            });
        }, folder.name, 'Rename folder');
        dialog.open();
    }

    const createDeleteFolderCallback = (dialogProvider: DialogProviderContextModel) => (folder: Folder) => {
        // after the folder is deleted, take the user to the deleted folder's parent
        // if the deleted folder doesn't have a parent, go to the "home" page
        const dialog = DialogFactory.buildDeleteFolderDialog(dialogProvider, () => {
            return new Promise((resolve, reject) => {
                dispatch(FolderStore.actionCreators.deleteFolder(folder.id, (success: boolean, errors?: string[]) => {
                    if (success) {
                        resolve([]);
                        dialog.close();
                        if (folder.parentId) {
                            navigate(`/folder/${folder.parentId}`);
                        } else {
                            navigate('/');
                        }
                    } else {
                        resolve(errors);
                    }
                }));
            });
        });
        dialog.open();
    }

    const createMoveFolderCallback = (dialogProvider: DialogProviderContextModel) => (folder: Folder) => {
        const dialog = DialogFactory.buildMoveFolderDialog(dialogProvider, folder.parentId, folder, (parentFolder?: Folder) => {
            return new Promise((resolve, reject) => {
                dispatch(FolderStore.actionCreators.moveFolder(folder.id, parentFolder ? parentFolder.id : undefined, (success: boolean, errors?: string[]) => {
                    if (success) {
                        resolve([]);
                        dialog.close();
                    } else {
                        resolve(errors);
                    }
                }));
            });
        });
        dialog.open();
    }

    const createDeleteImagesCallback = (dialogProvider: DialogProviderContextModel) => (images: Image[]) => {
        const dialog = DialogFactory.buildDeleteImagesDialog(dialogProvider, images, () => {
            return new Promise((resolve, reject) => {
                dispatch(ImageStore.actionCreators.deleteImages(images, (success: boolean, errors?: string[]) => {
                    if (success) {
                        resolve([]);
                        dialog.close();
                    } else {
                        resolve(errors);
                    }
                }));
            });
        });
        dialog.open();
    }

    const createMoveImagesCallback = (dialogProvider: DialogProviderContextModel) => (images: Image[]) => {
        if (!props.folderId) {
            console.error('Folder id is not defined. Cannot move images if we do not know where they currently are');
            return;
        }
        const dialog = DialogFactory.buildMoveImagesDialog(dialogProvider, props.folderId, images, (parentFolder?: Folder) => {
            return new Promise((resolve, reject) => {
                dispatch(ImageStore.actionCreators.moveImages(images, parentFolder ? parentFolder.id : undefined, (success: boolean, errors?: string[]) => {
                    if (success) {
                        resolve([]);
                        dialog.close();
                    } else {
                        resolve(errors);
                    }
                }));
            });
        });
        dialog.open();
    }

    const isImageSelected = (image: Image) => {
        return selectedImageIds.includes(image.id);
    }

    const allImagesSelected = () => {
        return images.length > 0 && images.length === selectedImageIds.length;
    }

    const handleCheckImage = (image: Image) => {
        if (!isImageSelected(image)) {
            setSelectedImageIds([...selectedImageIds, image.id]);
        } else {
            setSelectedImageIds(selectedImageIds.filter(i => i !== image.id))
        }
    }

    const handleToggleAllImages = () => {
        if (allImagesSelected()) {
            // deselect all images
            setSelectedImageIds([]);
        } else {
            // select all images
            setSelectedImageIds(images.map(i => i.id));
        }
    };

    const handleChangeView = (view: 'list' | 'grid') => {
        setView(view);
        localStorage.setItem('FOLDER_VIEW', view);
    }

    const handleDeselectAll = () => {
        setSelectedImageIds([]);
    }

    if (!folder) {
        return (<MessageView>Folder with id {props.folderId} was not found</MessageView>);
    }

    return (
        <DialogProviderContext.Consumer>
            {(dialogContext) =>
                <div>
                    {isLoading ? <LinearProgress /> : <div style={{ height: 4, display: 'block', width: '100%' }}></div>}
                    <FolderViewHeader
                        folder={folder}
                        images={images}
                        selectedImageIds={selectedImageIds}
                        onRefresh={handleRefresh}
                        view={view}
                        onRenameFolder={createRenameFolderCallback(dialogContext)}
                        onCreateFolder={createCreateFolderCallback(dialogContext)}
                        onDeleteFolder={createDeleteFolderCallback(dialogContext)}
                        onMoveFolder={createMoveFolderCallback(dialogContext)}
                        onDeleteImages={createDeleteImagesCallback(dialogContext)}
                        onMoveImages={createMoveImagesCallback(dialogContext)}
                        onChangeView={handleChangeView}
                        onDeselectAll={handleDeselectAll}

                    />
                    {view === 'grid' &&
                        <FilesGrid
                            folders={folder.childFolders}
                            images={images}
                            currentFolder={folder}
                            selectedImageIds={selectedImageIds}
                            onCheckImage={handleCheckImage}
                            onToggleAllImages={handleToggleAllImages}
                            onRefresh={handleRefresh}
                            onCreateFolder={createCreateFolderCallback(dialogContext)}
                            onDeleteFolder={createDeleteFolderCallback(dialogContext)}
                            onMoveFolder={createMoveFolderCallback(dialogContext)}
                            onDeleteImages={createDeleteImagesCallback(dialogContext)}
                            onMoveImages={createMoveImagesCallback(dialogContext)}
                        />
                    }
                    {view === 'list' &&
                        <FilesTable
                            folders={folder.childFolders}
                            images={images}
                            currentFolder={folder}
                            selectedImageIds={selectedImageIds}
                            onCheckImage={handleCheckImage}
                            onToggleAllImages={handleToggleAllImages}
                            onRefresh={handleRefresh}
                            onCreateFolder={createCreateFolderCallback(dialogContext)}
                            onDeleteFolder={createDeleteFolderCallback(dialogContext)}
                            onMoveFolder={createMoveFolderCallback(dialogContext)}
                            onDeleteImages={createDeleteImagesCallback(dialogContext)}
                            onMoveImages={createMoveImagesCallback(dialogContext)}
                        />
                    }
                </div>
            }
        </DialogProviderContext.Consumer>
    )
}

export default FolderView;